import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import NotificationsPopover from '@/Modal/NotificationsPopover';
import PartnerPortalModal from '@/Modal/PartnerPortalModal';
import { MessageCircle, Activity, Handshake, Radar, Menu } from 'lucide-react';
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setDashboardName, setSidebarCollapsed } from "@/Redux/Action/organizationActions";
import { CommanServices } from "@/services/commanServices";
import { Input } from "@/components/ui/input";
import { isWebAdmin } from "@/auth/webAccess";
import InstitutionSwitcher from "@/components/InstitutionSwitcher";
import {
  activeInstitutionId,
  institutionHeaderDisplayName,
  isPlatformSuperadmin,
  readStoredInstitution,
} from "@/auth/saasSession";
import { isFeatureEnabled, LICENSE_FEATURES } from "@/auth/featureEntitlements";
import "./SaaSShell.css";

const SAAS_LANGUAGE_KEY = 'disasterms_saas_language';
const SAAS_LANGUAGE_EVENT = 'disasterms:saas-language-changed';
const SAAS_INSTITUTION_TITLE_EVENT = 'disasterms:saas-institution-title-changed';
const SAAS_INSTITUTION_TITLE_KEY = 'disasterms_saas_selected_institution';
const SAAS_INSTITUTION_SELECTION_EVENT = 'disasterms:saas-institution-selected';
const SAAS_ADMINISTRATION_SECTION_EVENT = 'disasterms:saas-administration-section-selected';

const readSaaSLanguage = () => (
  typeof window !== 'undefined' && window.localStorage.getItem(SAAS_LANGUAGE_KEY) === 'ar' ? 'ar' : 'en'
);

const readSaaSInstitutionTitle = () => {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.localStorage.getItem(SAAS_INSTITUTION_TITLE_KEY) || 'null');
  } catch (_) {
    return null;
  }
};

const Header = ({ onOpenSidebar }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dashboardName } = useSelector((state) => state.organization);

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const [headerToggle, setHeaderToggle] = useState(false)
  const [partnerOpen, setPartnerOpen] = useState(false)
  const [userData, setUserData] = useState(() => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  });
  const [activeInstitution, setActiveInstitution] = useState(() => readStoredInstitution());
  const [saasLanguage, setSaaSLanguage] = useState(readSaaSLanguage);
  const [saasInstitutionTitle, setSaaSInstitutionTitle] = useState(readSaaSInstitutionTitle);
  const [saasSelectedInstitutionId, setSaaSSelectedInstitutionId] = useState(() => (
    new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get('institution') || ''
  ));
  const isAdmin = isWebAdmin(userData);
  const foresightEnabled = isFeatureEnabled(userData, LICENSE_FEATURES.FORESIGHT);
  const partnersEnabled = isFeatureEnabled(userData, LICENSE_FEATURES.PARTNERS);
  const isPlatformControlPlane = isPlatformSuperadmin(userData);
  const institutionScopedTitle = Boolean(activeInstitutionId(userData))
    && !isPlatformSuperadmin(userData);
  const institutionDisplayName = institutionHeaderDisplayName(userData, activeInstitution);
  const locationSearch = location.search || '';
  const selectedSaaSInstitutionId = new URLSearchParams(locationSearch).get('institution') || saasSelectedInstitutionId;
  const isSaaSMarketplace = location.pathname.startsWith('/saas-administration')
    && new URLSearchParams(locationSearch).get('section') === 'marketplace';
  const isReportsRoute = location.pathname === '/reports';
  const isArabicSaaS = saasLanguage === 'ar';
  const saasPageTitle = location.pathname === '/notifications'
    ? (isArabicSaaS ? 'الإشعارات' : 'Notifications')
    : location.pathname === '/profile'
      ? (isArabicSaaS ? 'الملف الشخصي' : 'User Profile')
      : isSaaSMarketplace
        ? (isArabicSaaS ? 'المتجر' : 'Marketplace')
        : selectedSaaSInstitutionId
          ? (String(saasInstitutionTitle?.institutionId || '') === String(selectedSaaSInstitutionId)
            ? saasInstitutionTitle.displayName
            : (isArabicSaaS ? 'المؤسسة' : 'Institution'))
          : (isArabicSaaS ? 'إدارة المنصة' : 'SaaS Administration');
  const headerTitle = isPlatformControlPlane
    ? saasPageTitle
    : institutionScopedTitle
      ? (institutionDisplayName || 'Dashboard')
      : dashboardName;

  const syncUserFromStorage = () => {
    try {
      const user = localStorage.getItem('user');
      setUserData(user ? JSON.parse(user) : null);
    } catch {
      setUserData(null);
    }
  };

  const getLoginDepartment = async () => {
    try {
      await CommanServices.getLoginDepartment().then((res) => {
        if (res.status) {
          setDepartmentOptions(res.data)
        }
      })
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    const path = location.pathname || "";
    if (location.pathname.includes('incident-details')) {
      dispatch(setSidebarCollapsed(true));
      setHeaderToggle(true);
    } else {
      setHeaderToggle(false);
    }
    // NOTE: we intentionally do NOT auto-expand the sidebar on other route changes
  }, [location.pathname]);

  useEffect(() => {
    if (!isPlatformControlPlane) getLoginDepartment();
  }, [isPlatformControlPlane])

  useEffect(() => {
    const handleSessionUpdated = () => {
      syncUserFromStorage();
      setActiveInstitution(readStoredInstitution());
    };
    const handleSaaSLanguage = (event) => {
      setSaaSLanguage(event.detail?.language === 'ar' ? 'ar' : 'en');
    };
    const handleSaaSInstitutionTitle = (event) => {
      if (event.detail?.institutionId) setSaaSInstitutionTitle(event.detail);
    };
    const handleSaaSInstitutionSelection = (event) => {
      setSaaSSelectedInstitutionId(String(event.detail?.institutionId || ''));
    };
    const handleSaaSSection = (event) => {
      if (event.detail?.section === 'institutions' || event.detail?.section === 'marketplace') {
        setSaaSSelectedInstitutionId('');
      }
    };
    window.addEventListener('userProfileUpdated', handleSessionUpdated);
    window.addEventListener('saas-session-changed', handleSessionUpdated);
    window.addEventListener(SAAS_LANGUAGE_EVENT, handleSaaSLanguage);
    window.addEventListener(SAAS_INSTITUTION_TITLE_EVENT, handleSaaSInstitutionTitle);
    window.addEventListener(SAAS_INSTITUTION_SELECTION_EVENT, handleSaaSInstitutionSelection);
    window.addEventListener(SAAS_ADMINISTRATION_SECTION_EVENT, handleSaaSSection);
    return () => {
      window.removeEventListener('userProfileUpdated', handleSessionUpdated);
      window.removeEventListener('saas-session-changed', handleSessionUpdated);
      window.removeEventListener(SAAS_LANGUAGE_EVENT, handleSaaSLanguage);
      window.removeEventListener(SAAS_INSTITUTION_TITLE_EVENT, handleSaaSInstitutionTitle);
      window.removeEventListener(SAAS_INSTITUTION_SELECTION_EVENT, handleSaaSInstitutionSelection);
      window.removeEventListener(SAAS_ADMINISTRATION_SECTION_EVENT, handleSaaSSection);
    };
  }, []);

  useEffect(() => {
    const queryInstitutionId = new URLSearchParams(location.search || '').get('institution') || '';
    if (queryInstitutionId) setSaaSSelectedInstitutionId(queryInstitutionId);
    else setSaaSSelectedInstitutionId('');
  }, [isSaaSMarketplace, location.pathname, location.search]);

  const handleStatusClick = () => {
    navigate('/status');
  };

  return (
    <div
      className={`${isPlatformControlPlane
        ? 'dms-saas-shell h-[72px] border-b border-white/[0.08] bg-[linear-gradient(90deg,#0c1230,#101a44)] px-7 py-0 shadow-none'
        : isReportsRoute
          ? 'bg-[linear-gradient(90deg,#0f172a_0%,#1e3a8a_100%)] border-b border-transparent px-6 py-4 shadow-none'
          : 'bg-gradient-to-r from-gray-900 to-blue-900 border-b border-blue-800 px-6 py-4 shadow-md'} flex-shrink-0 ${headerToggle ? 'hidden' : ''}`}
      dir={isPlatformControlPlane ? (isArabicSaaS ? 'rtl' : 'ltr') : undefined}
      data-testid={isPlatformControlPlane ? 'saas-platform-topbar' : undefined}
    >
      <div className={`flex items-center justify-between ${isPlatformControlPlane ? 'h-full' : 'h-12'}`}>
        {/* Left: hamburger + title */}
        <div className="flex min-w-0 flex-1 items-center">
          {!isPlatformControlPlane && onOpenSidebar && (
            <button
              type="button"
              onClick={onOpenSidebar}
              className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] border border-white/20 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 lg:hidden"
              aria-label="Open navigation menu"
              title="Open navigation menu"
              data-testid="institution-mobile-sidebar-trigger"
            >
              <Menu className="h-5 w-5" strokeWidth={1.8} />
            </button>
          )}
          {!isPlatformControlPlane && !institutionScopedTitle && isEditingTitle ? (
            <Input
              value={dashboardName}
              onChange={(e) => {
                dispatch(setDashboardName(e.target.value));
              }}
              onBlur={() => setIsEditingTitle(false)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingTitle(false);
                }
              }}
              onFocus={(e) => e.target.select()}
              className="text-2xl font-bold text-white bg-transparent border-white/30 focus:border-white w-64 h-10"
              autoFocus
            />
          ) : (
            <h1
              className={`${isPlatformControlPlane ? 'min-w-0 truncate text-[19px] tracking-[-0.01em]' : 'text-2xl'} font-bold text-white transition-colors flex items-center h-10 ${institutionScopedTitle || isPlatformControlPlane ? '' : 'cursor-pointer hover:text-gray-200'}`}
              onClick={() => {
                if (!institutionScopedTitle && !isPlatformControlPlane) setIsEditingTitle(true);
              }}
              title={isPlatformControlPlane
                ? (isArabicSaaS ? 'لوحة التحكم بالمنصة' : 'Platform control plane')
                : institutionScopedTitle
                  ? 'Active institution'
                  : 'Click to customize dashboard name'}
            >
              {headerTitle}
            </h1>
          )}
        </div>
        {/* Right: actions */}
        <div className="flex shrink-0 items-center gap-2">
          {!isPlatformControlPlane && (
            <>
              <InstitutionSwitcher onActiveInstitutionChange={setActiveInstitution} />
              <div className="hidden sm:flex items-center space-x-2">
            {/* <button className=" inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:text-accent-foreground py-2 text-white hover:bg-white/10 h-10 px-3" title="Consult an Expert">
                <MessageCircle className="h-4 w-4 mr-1" />
                <span className="hidden lg:inline text-sm">Expert</span>
              </button> */}

            {/* Status Button - Admin Only */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                {foresightEnabled && (
                  <button
                    onClick={() => navigate('/foresight')}
                    data-testid="foresight-btn"
                    className="relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 py-2 text-white hover:bg-white/10 h-10 px-3"
                    title="ForeSight — early warning intelligence"
                  >
                    <Radar className="h-4 w-4 mr-1" />
                    <span className="hidden lg:inline text-sm">ForeSight</span>
                  </button>
                )}
                {partnersEnabled && (
                  <button
                    onClick={() => setPartnerOpen(true)}
                    data-testid="partner-portal-btn"
                    className="relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 py-2 text-white hover:bg-white/10 h-10 px-3"
                    title="Strategic Partner Portal"
                  >
                    <Handshake className="h-4 w-4 mr-1" />
                    <span className="hidden lg:inline text-sm">Partners</span>
                  </button>
                )}
                <button
                  onClick={handleStatusClick}
                  className="relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:text-accent-foreground py-2 text-white hover:bg-white/10 h-10 px-3"
                  title="View Duty Status"
                >
                  <Activity className="h-4 w-4 mr-1" />
                  <span className="hidden lg:inline text-sm">Status</span>
                </button>
              </div>
            )}
              </div>

              {/* Notifications - the component can accept props if you want to wire counts */}
              <div className="ml-1">
                <NotificationsPopover />
              </div>
              <div className="border-l border-gray-300 h-6 mx-2"></div>
            </>
          )}

          {/* User avatar */}
          {/* <Button variant="ghost" className="text-white hover:bg-white/10 h-9 w-9 p-0 rounded-full"> */}
          <button
            type="button"
            className={`flex h-10 cursor-pointer items-center rounded-[10px] transition-colors ${isPlatformControlPlane
              ? 'gap-2.5 border-0 bg-transparent px-2.5 hover:bg-white/[0.035] focus-visible:outline-none focus-visible:[&_p]:text-[#28ade3]'
              : 'space-x-3 px-3 py-2 hover:bg-white/10'}`}
            onClick={() => navigate('/profile')}
            title={isPlatformControlPlane && isArabicSaaS ? 'عرض الملف الشخصي' : 'View profile'}
            aria-label={isPlatformControlPlane && isArabicSaaS ? 'عرض الملف الشخصي' : 'View profile'}
            data-testid="header-profile-link"
          >
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-white/20 ${isPlatformControlPlane ? 'bg-[#163e8e]' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}>
              <span className="text-white text-sm font-semibold">
                {userData?.full_name ? userData?.full_name?.charAt(0)?.toUpperCase() : ''}
              </span>
            </div>
            <div className="hidden md:block">
              <p className="whitespace-nowrap text-sm font-medium leading-tight text-white">
                {isPlatformControlPlane
                  ? (isArabicSaaS ? 'مسؤول منصة SaaS' : 'SaaS Platform Administrator')
                  : userData?.full_name}
              </p>
              {!isPlatformControlPlane && (
                <p className="text-xs leading-tight text-gray-300">{departmentOptions?.find(de => de.id == (userData?.departments_units?.[0] || userData?.department_unit))?.department_name || ''}</p>
              )}
            </div>
          </button>
          {/* </Button> */}
        </div>
      </div>
      {!isPlatformControlPlane && partnersEnabled && (
        <PartnerPortalModal open={partnerOpen} onClose={() => setPartnerOpen(false)} />
      )}
    </div>
  )
}

export default Header
